import type { paths } from "./generated/tester";
import { ApiError, networkError, toApiError } from "./errors";
import { createApiClient, TESTER_BASE_URL, unwrap } from "./http";
import type { ReportDocument } from "./types/report";
import type {
  AgentArtifact,
  AgentReleases,
  AgentSource,
  AiSettings,
  AiSettingsInput,
  AiTestResult,
  ArtifactList,
  ArtifactResolution,
  AuditList,
  CatalogProfile,
  CatalogProfileDetail,
  CatalogTest,
  CatalogTestDetail,
  CommandAccepted,
  EnhanceRequest,
  Environment,
  EnvironmentCheck,
  EventList,
  ExecutionDetail,
  Health,
  JiraIssueRequest,
  JiraIssueResult,
  JiraIssueTypeList,
  JiraProjectList,
  JiraSettings,
  JiraSettingsInput,
  JiraTestResult,
  LogLines,
  MarkdownDocument,
  PreviewResponse,
  RecordedRequest,
  RequestList,
  RunList,
  RunRequest,
  RunShare,
  RunSnapshot,
  StatsSummary,
  Version,
} from "./types";

const client = createApiClient<paths>(TESTER_BASE_URL);

const EVENT_PAGE = 1000;

export type RunStatusFilter = NonNullable<NonNullable<paths["/runs"]["get"]["parameters"]["query"]>["status"]>;

export interface RunListFilters {
  status?: RunStatusFilter;
  agent?: string;
  source?: AgentSource;
  started_by?: string;
  from?: string;
  to?: string;
  q?: string;
  sort?: "started" | "duration";
  agent_artifact_id?: string;
  limit?: number;
  cursor?: string;
}

export type ArtifactFilters = NonNullable<paths["/artifacts"]["get"]["parameters"]["query"]>;

export interface UploadInput {
  file: File;
  agent: string;
  source: "development" | "master";
  commit_sha?: string;
  build_id?: string;
}

export interface AuditFilters {
  from?: string;
  to?: string;
  actor?: string;
  actor_type?: "USER" | "SYSTEM";
  action?: string;
  resource_type?: string;
  resource_id?: string;
  run_id?: string;
  test_id?: string;
  result?: string;
  machine_instance_id?: string;
  request_id?: string;
  correlation_id?: string;
  q?: string;
  cursor?: string;
  limit?: number;
}

export const testerApi = {
  health: () => unwrap<Health>(client.GET("/health")),
  version: () => unwrap<Version>(client.GET("/version")),
  environment: () => unwrap<Environment>(client.GET("/environment")),
  runOcSanity: (profile_id: string) => unwrap<EnvironmentCheck>(client.POST("/environment/oc-sanity", { body: { profile_id } })),
  catalogTests: () => unwrap<CatalogTest[]>(client.GET("/catalog/tests")),
  catalogTest: (id: string) => unwrap<CatalogTestDetail>(client.GET("/catalog/tests/{test_id}", { params: { path: { test_id: id } } })),
  catalogProfiles: () => unwrap<CatalogProfile[]>(client.GET("/catalog/profiles")),
  catalogProfile: (id: string) =>
    unwrap<CatalogProfileDetail>(client.GET("/catalog/profiles/{profile_id}", { params: { path: { profile_id: id } } })),
  releases: (agent: string) => unwrap<AgentReleases>(client.GET("/agents/{agent}/releases", { params: { path: { agent } } })),
  artifacts: (q: ArtifactFilters = {}) => unwrap<ArtifactList>(client.GET("/artifacts", { params: { query: q } })),
  artifact: (id: string) => unwrap<AgentArtifact>(client.GET("/artifacts/{artifact_id}", { params: { path: { artifact_id: id } } })),
  resolveArtifact: (body: { agent: string; version?: string; url?: string }) =>
    unwrap<ArtifactResolution>(client.POST("/artifacts/resolve", { body })),
  resolution: (id: string) =>
    unwrap<ArtifactResolution>(client.GET("/artifacts/resolutions/{resolution_id}", { params: { path: { resolution_id: id } } })),
  previewRun: (body: RunRequest) => unwrap<PreviewResponse>(client.POST("/runs/preview", { body })),
  startRun: (body: RunRequest & { command_id: string }) => unwrap<CommandAccepted>(client.POST("/runs", { body })),
  cancelRun: (id: string, command_id: string) =>
    unwrap<CommandAccepted>(client.POST("/runs/{run_id}/cancel", { params: { path: { run_id: id } }, body: { command_id } })),
  runs: (q: RunListFilters = {}) => unwrap<RunList>(client.GET("/runs", { params: { query: q } })),
  run: (id: string) => unwrap<RunSnapshot>(client.GET("/runs/{run_id}", { params: { path: { run_id: id } } })),
  runShare: (id: string) => unwrap<RunShare>(client.GET("/runs/{run_id}/share", { params: { path: { run_id: id } } })),
  shareRun: (id: string) => unwrap<RunShare>(client.POST("/runs/{run_id}/share", { params: { path: { run_id: id } } })),
  runEvents: async (id: string, after?: string): Promise<EventList> => {
    const items: EventList["items"] = [];
    let cursor = after;
    for (;;) {
      const page = await unwrap<EventList>(
        client.GET("/runs/{run_id}/events", { params: { path: { run_id: id }, query: { after: cursor, limit: EVENT_PAGE } } }),
      );
      items.push(...page.items);
      if (page.items.length < EVENT_PAGE) return { items };
      cursor = page.items.at(-1)!.event_id;
    }
  },
  reportJson: async (id: string) =>
    (await unwrap(
      client.GET("/runs/{run_id}/report", { params: { path: { run_id: id }, query: { format: "json", view: true } } }),
    )) as unknown as ReportDocument,
  reportBlob: async (id: string, format: "json" | "html" | "md"): Promise<Blob> => {
    const { data } = await client.GET("/runs/{run_id}/report", {
      params: { path: { run_id: id }, query: { format } },
      parseAs: "blob",
    });
    return data as unknown as Blob;
  },
  reportMarkdown: (id: string) =>
    unwrap<string>(
      client.GET("/runs/{run_id}/report", { params: { path: { run_id: id }, query: { format: "md", view: true } }, parseAs: "text" }),
    ),
  bugReport: (id: string) =>
    unwrap<MarkdownDocument>(client.GET("/executions/{execution_id}/bug-report", { params: { path: { execution_id: id } } })),
  execution: (id: string) => unwrap<ExecutionDetail>(client.GET("/executions/{execution_id}", { params: { path: { execution_id: id } } })),
  requests: (id: string, q: { route?: string; after?: number; limit?: number; cursor?: string } = {}) =>
    unwrap<RequestList>(client.GET("/executions/{execution_id}/requests", { params: { path: { execution_id: id }, query: q } })),
  request: (id: string, requestId: string) =>
    unwrap<RecordedRequest>(
      client.GET("/executions/{execution_id}/requests/{request_id}", {
        params: { path: { execution_id: id, request_id: requestId } },
      }),
    ),
  logs: (id: string, q: { source: "agent" | "application"; tail?: number; q?: string; offset?: number }) =>
    unwrap<LogLines>(client.GET("/executions/{execution_id}/logs", { params: { path: { execution_id: id }, query: q } })),
  evidenceContent: async (evidenceId: string, range?: [number, number]): Promise<Blob> => {
    const { data } = await client.GET("/evidence/{evidence_id}/content", {
      params: { path: { evidence_id: evidenceId } },
      headers: range ? { Range: `bytes=${range[0]}-${range[1]}` } : {},
      parseAs: "blob",
    });
    return data as unknown as Blob;
  },
  stats: (range: "7d" | "30d" | "90d") => unwrap<StatsSummary>(client.GET("/stats/summary", { params: { query: { range } } })),
  audit: (q: AuditFilters = {}) => unwrap<AuditList>(client.GET("/audit", { params: { query: q } })),
  aiSettings: () => unwrap<AiSettings>(client.GET("/settings/ai")),
  saveAiSettings: (body: AiSettingsInput) => unwrap<AiSettings>(client.PUT("/settings/ai", { body })),
  deleteAiSettings: async (): Promise<void> => {
    await unwrap<unknown>(client.DELETE("/settings/ai"));
  },
  testAiSettings: () => unwrap<AiTestResult>(client.POST("/settings/ai/test")),
  enhance: (body: EnhanceRequest) => unwrap<MarkdownDocument>(client.POST("/ai/enhance", { body })),
  jiraSettings: () => unwrap<JiraSettings>(client.GET("/settings/jira")),
  saveJiraSettings: (body: JiraSettingsInput) => unwrap<JiraSettings>(client.PUT("/settings/jira", { body })),
  deleteJiraSettings: async (): Promise<void> => {
    await unwrap<unknown>(client.DELETE("/settings/jira"));
  },
  testJiraSettings: () => unwrap<JiraTestResult>(client.POST("/settings/jira/test")),
  jiraProjects: (q = "") => unwrap<JiraProjectList>(client.GET("/integrations/jira/projects", { params: { query: q ? { q } : {} } })),
  jiraIssueTypes: (projectKey: string) =>
    unwrap<JiraIssueTypeList>(
      client.GET("/integrations/jira/projects/{project_key}/issue-types", { params: { path: { project_key: projectKey } } }),
    ),
  createJiraIssue: (body: JiraIssueRequest, idempotencyKey: string) =>
    unwrap<JiraIssueResult>(client.POST("/integrations/jira/issues", { params: { header: { "Idempotency-Key": idempotencyKey } }, body })),
  uploadArtifact: (input: UploadInput, onProgress: (pct: number) => void, signal?: AbortSignal) =>
    uploadWithProgress(input, onProgress, signal),
};

let uploadToken: (force?: boolean) => Promise<string | null> = async () => null;

export function setUploadTokenProvider(fn: (force?: boolean) => Promise<string | null>) {
  uploadToken = fn;
}

async function uploadWithProgress(input: UploadInput, onProgress: (pct: number) => void, signal?: AbortSignal) {
  const form = new FormData();
  form.set("file", input.file);
  form.set("agent", input.agent);
  form.set("source", input.source);
  if (input.commit_sha) form.set("commit_sha", input.commit_sha);
  if (input.build_id) form.set("build_id", input.build_id);
  const token = await uploadToken();
  try {
    return await sendUpload(form, token, onProgress, signal);
  } catch (e) {
    if (!token || !(e instanceof ApiError) || e.status !== 401) throw e;
  }
  try {
    return await sendUpload(form, await uploadToken(true), onProgress, signal);
  } catch (e) {
    if (e instanceof ApiError && e.status === 401) window.dispatchEvent(new CustomEvent("ats:session-expired"));
    throw e;
  }
}

function sendUpload(form: FormData, token: string | null, onProgress: (pct: number) => void, signal?: AbortSignal) {
  return new Promise<AgentArtifact>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `${TESTER_BASE_URL}/artifacts`);
    if (token) xhr.setRequestHeader("Authorization", `Bearer ${token}`);
    xhr.setRequestHeader("X-Request-ID", crypto.randomUUID());
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = async () => {
      const res = new Response(xhr.responseText, {
        status: xhr.status,
        headers: { "content-type": "application/json", "x-request-id": xhr.getResponseHeader("x-request-id") ?? "" },
      });
      if (xhr.status >= 200 && xhr.status < 300) resolve((await res.json()) as AgentArtifact);
      else reject(await toApiError(res));
    };
    xhr.onerror = () => reject(networkError(new TypeError("upload failed")));
    xhr.onabort = () => reject(new ApiError(0, "NETWORK", "Upload cancelled.", ""));
    signal?.addEventListener("abort", () => xhr.abort());
    xhr.send(form);
  });
}
